import {
  Column,
  ColumnOrderState,
  ColumnSizingState,
  OnChangeFn,
  Row,
  SortingState,
  TableState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Center, Divider, Loader, Paper, Table } from "@mantine/core"
import { IconArrowDown, IconArrowUp } from "@tabler/icons-react"
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useUserLocalStorage } from "../../app/-components/use-user-local-storage"
import { ColumnSettingsMenu } from "./ColumnSettingsMenu"
import {
  getColumnWidthStyles,
  getDefaultColumnOrder,
  getExplicitlySizedColumnIds,
} from "./functions"
import { BaseTableProps, TheasoftColumnDef } from "./types"
import "./index.css"

export function BaseVirtualizedTable<TData>({
  data,
  columnDefinitions,
  getRowProps,
  sortingState,
  tableContainerProps,
  defaultColumnVisibility,
  isLoading = false,
  onTableScroll,
  id,
  tableOptions,
  withSettingsMenu = true,
}: BaseTableProps<TData>) {
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [sorting, setSorting] = sortingState ?? useState<SortingState>([])
  const columnOrderingEnabled = withSettingsMenu

  const columns = useMemo<TheasoftColumnDef<TData>[]>(
    () => columnDefinitions,
    [columnDefinitions]
  )
  const defaultColumnOrder = useMemo(
    () => getDefaultColumnOrder(columnDefinitions),
    [columnDefinitions]
  )
  const explicitlySizedColumnIds = useMemo(
    () => getExplicitlySizedColumnIds(columnDefinitions),
    [columnDefinitions]
  )

  const [storedTableState, setStoredTableState] = useUserLocalStorage<
    Partial<TableState>
  >({
    key: `table-settings-${id}`,
    defaultValue: {
      columnVisibility: defaultColumnVisibility ?? {},
      ...(columnOrderingEnabled ? { columnOrder: defaultColumnOrder } : {}),
    },
  })

  const {
    columnOrder: storedColumnOrder,
    ...storedTableStateWithoutColumnOrder
  } = storedTableState

  const handleResetColumnOrder = () => {
    setStoredTableState((prev) => ({
      ...prev,
      columnOrder: defaultColumnOrder,
    }))
  }

  const handleResetColumnVisibility = () => {
    setStoredTableState((prev) => ({
      ...prev,
      columnVisibility: defaultColumnVisibility ?? {},
    }))
  }

  useEffect(() => {
    if (tableContainerRef.current && onTableScroll) {
      onTableScroll(tableContainerRef.current)
    }
  }, [onTableScroll, data.length])

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    debugTable: import.meta.env.DEV,
    columnResizeMode: "onChange",
    ...tableOptions,
  })
  const columnResizingEnabled = table.options.enableColumnResizing !== false

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    storedTableState.columnSizing ?? {}
  )

  const hasExplicitColumnSize = useCallback(
    (column: Column<TData, unknown>) => explicitlySizedColumnIds.has(column.id),
    [explicitlySizedColumnIds]
  )

  const shouldUseFixedColumnWidth = useCallback(
    (column: Column<TData, unknown>) =>
      columnResizingEnabled || hasExplicitColumnSize(column),
    [columnResizingEnabled, hasExplicitColumnSize]
  )

  const resolveColumnWidthStyles = useCallback(
    (column: Column<TData, unknown>): CSSProperties => {
      return getColumnWidthStyles(column, shouldUseFixedColumnWidth(column))
    },
    [shouldUseFixedColumnWidth]
  )

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 36.69,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  })

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting =
      typeof updater === "function"
        ? updater(table.getState().sorting)
        : updater
    setSorting(newSorting)
    if (!!table.getRowModel().rows.length) {
      rowVirtualizer.scrollToIndex?.(0)
    }
  }

  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = (
    updater
  ) => {
    const newVisibility =
      typeof updater === "function"
        ? updater(table.getState().columnVisibility)
        : updater
    setStoredTableState((prev) => ({
      ...prev,
      columnVisibility: newVisibility,
    }))
  }

  const handleColumnResize: OnChangeFn<ColumnSizingState> = (updater) => {
    setColumnSizing(updater)

    const newSizing =
      typeof updater === "function"
        ? updater(table.getState().columnSizing)
        : updater
    setStoredTableState((prev) => ({
      ...prev,
      columnSizing: newSizing,
    }))
  }

  const handleColumnOrderChange: OnChangeFn<ColumnOrderState> = (updater) => {
    if (!columnOrderingEnabled) {
      return
    }

    const newColumnOrder =
      typeof updater === "function"
        ? updater(table.getState().columnOrder)
        : updater
    setStoredTableState((prev) => ({
      ...prev,
      columnOrder: newColumnOrder,
    }))
  }

  table.setOptions((prev) => {
    return {
      ...prev,
      onSortingChange: handleSortingChange,
      onColumnVisibilityChange: handleColumnVisibilityChange,
      onColumnSizingChange: columnResizingEnabled
        ? handleColumnResize
        : undefined,
      onColumnOrderChange: columnOrderingEnabled
        ? handleColumnOrderChange
        : undefined,
      state: {
        ...prev.state,
        ...storedTableStateWithoutColumnOrder,
        ...(columnOrderingEnabled
          ? { columnOrder: storedColumnOrder ?? defaultColumnOrder }
          : {}),
        columnSizing: columnResizingEnabled ? columnSizing : {},
      },
    }
  })

  return (
    <Paper
      withBorder
      shadow="xs"
      onScroll={(e) => {
        onTableScroll?.(e.currentTarget)
      }}
      ref={tableContainerRef}
      className="infinite-scrolling-table"
      style={{
        overflow: "auto",
        scrollbarWidth: "thin",
        position: "relative",
      }}
      {...tableContainerProps}
    >
      {withSettingsMenu && (
        <ColumnSettingsMenu
          table={table}
          onColumnOrderChange={(updater) => {
            handleColumnOrderChange(updater)
          }}
          onResetColumnOrder={handleResetColumnOrder}
          onResetColumnVisibility={handleResetColumnVisibility}
        />
      )}
      {/* Even though we're still using sematic table tags, we must use CSS grid and flexbox for dynamic row heights */}
      <Table stickyHeader display={"grid"}>
        <Table.Thead display={"grid"}>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Tr
              key={headerGroup.id}
              style={{ display: "flex", width: "100%" }}
            >
              {headerGroup.headers.map((header) => {
                const columnWidthStyles = resolveColumnWidthStyles(
                  header.column
                )
                return (
                  <Table.Th
                    {...(header.column.columnDef as TheasoftColumnDef<TData>)
                      .headerProps}
                    key={header.id}
                    style={{
                      display: "flex",
                      ...columnWidthStyles,
                      minWidth: 0,
                      overflow: "hidden",
                      ...((header.column.columnDef as TheasoftColumnDef<TData>)
                        .headerProps?.style ?? {}),
                      justifyContent: "space-between",
                    }}
                    px={5}
                    py={2}
                  >
                    <div
                      {...(sortingState && {
                        onClick: header.column.getToggleSortingHandler(),
                        style: {
                          cursor: header.column.getCanSort()
                            ? "pointer"
                            : "default",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        },
                      })}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: <IconArrowUp size={12} />,
                        desc: <IconArrowDown size={12} />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      style={{
                        alignItems: "stretch",
                        cursor: header.column.getCanResize()
                          ? "col-resize"
                          : "default",
                        display: "flex",
                        margin: "0 -6px",
                        padding: "0 6px",
                        touchAction: "none",
                      }}
                    >
                      {header.column.getCanResize() && (
                        <Divider
                          orientation="vertical"
                          style={{
                            cursor: header.column.getCanResize()
                              ? "col-resize"
                              : "default",
                          }}
                        />
                      )}
                    </div>
                  </Table.Th>
                )
              })}
            </Table.Tr>
          ))}
        </Table.Thead>
        {isLoading ? (
          <Table.Tbody display={"grid"}>
            <Table.Tr display={"flex"}>
              <Table.Td>
                <Center p={"md"} h={"100%"} w={"100%"}>
                  <Loader />
                </Center>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        ) : (
          <Table.Tbody
            display={"grid"}
            h={rowVirtualizer.getTotalSize()}
            pos={"relative"}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index] as Row<TData>
              const rowProps = getRowProps?.(row)
              return (
                <Table.Tr
                  data-index={virtualRow.index}
                  ref={(node) => rowVirtualizer.measureElement(node)}
                  key={row.id}
                  display={"flex"}
                  pos={"absolute"}
                  w={"100%"}
                  {...rowProps}
                  style={{
                    cursor: rowProps?.onClick ? "pointer" : "default",
                    transform: `translateY(${virtualRow.start}px)`,
                    ...rowProps?.style,
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const fixedColumnWidth = shouldUseFixedColumnWidth(
                      cell.column
                    )
                    const columnWidthStyles = resolveColumnWidthStyles(
                      cell.column
                    )
                    return (
                      <Table.Td
                        {...(cell.column.columnDef as TheasoftColumnDef<TData>)
                          .cellProps}
                        key={cell.id}
                        w={fixedColumnWidth ? cell.column.getSize() : undefined}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          minWidth: 0,
                          ...columnWidthStyles,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          ...((
                            cell.column.columnDef as TheasoftColumnDef<TData>
                          ).cellProps?.style ?? {}),
                        }}
                        px={5}
                        py={2}
                      >
                        <div
                          style={{
                            minWidth: 0,
                            width: "100%",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </Table.Td>
                    )
                  })}
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        )}
      </Table>
    </Paper>
  )
}
